import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import { HttpClient, HttpErrorResponse, HttpHeaders, HttpParams } from '@angular/common/http';
import { Observable, of } from 'rxjs';

import { Offer, OfferFilters, OffersResponse } from '@app/core/_models/offer-interface';
import { environment } from '@env/environment';
import { BaseParamsService } from '@app/core/base-params.service';
import { SignedInOrAutoLoginGuard } from '@app/core/guards/signed-in-or-auto-login.guard';
import { catchError, map, switchMap } from 'rxjs/operators';

const appendAll = (params: HttpParams, key: string, values: (string | number)[] = []): HttpParams =>
  values.reduce((all, value) => all.append(key, value), params);

const PAGINATION_DEFAULTS = {
  perPage: '60',
  pageNumber: '1',
};
const offersOrderByParams = {
  usable: 'usable DESC',
  collection: 'collection_position ASC',
  until: 'usable_until ASC',
  since: 'usable_since ASC',
};

@Injectable()
export class OffersHttpService {
  private http = inject(HttpClient);
  private router = inject(Router);
  private baseParamsService = inject(BaseParamsService);
  private authGuard = inject(SignedInOrAutoLoginGuard);


  public getWithQuery(filters: Partial<OfferFilters> = {}): Observable<OffersResponse> {
    const params = this.prepareParams(filters);
    return this.http.get<OffersResponse>(
      this.getBaseUrl(),
      {
        ...this.prepareHeaders(),
        params,
      },
    );
  }

  public getById(id: number | string): Observable<Offer | null> {
    return this.http.get(
      `${this.getBaseUrl()}/${id}`,
      this.prepareHeaders(),
    ).pipe(
      map((response: any) => response.offer),
      catchError(e => this.handleError(e)),
    );
  }

  public getOffersMetadata(): Observable<any> {
    return this.http.get(
      `${this.getBaseUrl()}/meta`,
      this.prepareHeaders(),
    );
  }

  public useOffer(id: number | string): Observable<Offer | null> {
    return this.http.post(
      `${this.getBaseUrl()}/${id}/use`,
      {},
      this.prepareHeaders(),
    ).pipe(
      switchMap(() => this.getById(id)),
    );
  }

  private prepareParams(filters: Partial<OfferFilters>): HttpParams {
    let params = new HttpParams();
    params = params.append('order_by', this.offersOrderByParam(!!filters.collectionId));

    params = appendAll(params, 'tags[]', filters.tags);
    params = appendAll(params, 'stores[]', filters.shops);
    params = appendAll(params, 'ids[]', filters.ids);
    if (filters.collectionId) {
      params = params.append('collection_ids[]', filters.collectionId);
    }
    if (filters.campaignId) {
      params = params.append('campaign_id', filters.campaignId);
    }
    params = params.append('include_pagination_info', 'true');
    params = params.append('per_page', filters.perPage || PAGINATION_DEFAULTS.perPage);
    params = params.append('page_no', filters.page || PAGINATION_DEFAULTS.pageNumber);
    return params;
  }

  private prepareHeaders(): { headers: HttpHeaders } {
    return {
      headers: this.baseParamsService.getAuthHeadersWithAutologin() || new HttpHeaders(),
    };
  }

  private getBaseUrl(): string {
    const baseUrl = `${environment.webforms_api}/v3/${this.baseParamsService.slug}/members/`;
    return baseUrl + (this.authGuard.isAuthorizedToUseOffer() ? 'me/offers' : 'guest/offers');
  }

  private handleError(e: HttpErrorResponse): Observable<null> {
    e.status === 404
      ? this.router.navigate(['/', '404'], { skipLocationChange: true })
      : this.router.navigate(['/']);
    return of(null);
  }

  private offersOrderByParam(isSingleCollection: boolean): string {
    let fields = [offersOrderByParams.until, offersOrderByParams.since];

    if (isSingleCollection) { fields = [offersOrderByParams.collection, ...fields]; }
    if (this.authGuard.isAuthorizedToUseOffer()) { fields = [offersOrderByParams.usable, ...fields]; }

    return fields.join(', ');
  }
}
